import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminJson } from "../utils/adminSession";
import { activityLineWithoutWhen, formatAdminDateTime } from "../utils/formatAdminDate";
import AdminConfirmDialog from "../components/AdminConfirmDialog";

function AdminActivitiesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [savingId, setSavingId] = useState("");
  const [restoreTarget, setRestoreTarget] = useState(null);

  async function load(nextPage = page) {
    setLoading(true);
    setError("");
    try {
      const data = await adminJson(`/logs?page=${nextPage}`);
      setItems(Array.isArray(data.items) ? data.items : []);
      setPage(data.page || nextPage);
      setPageCount(data.pageCount || 1);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err.message || "Unable to load activities");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  function requestRestore(item) {
    setRestoreTarget(item);
  }

  async function confirmRestore() {
    const item = restoreTarget;
    if (!item) return;
    setSavingId(item._id);
    setError("");
    setNotice("");
    try {
      await adminJson(`/logs/${item._id}/restore`, { method: "POST" });
      setNotice(`${item.name || "Item"} restored.`);
      setRestoreTarget(null);
      await load(page);
    } catch (err) {
      if (err.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err.message || "Unable to restore item");
    } finally {
      setSavingId("");
    }
  }

  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400">Audit</p>
      <h1 className="mt-1 text-2xl font-semibold text-at-dark">Recent Activities</h1>
      <p className="mt-1 text-sm text-neutral-500">
        Create, update, delete, form and admin events from the last 30 days. Deleted items can be restored until then.
      </p>

      {error ? <p className="mt-4 text-sm text-red-600">{error}</p> : null}
      {notice ? <p className="mt-4 text-sm text-emerald-700">{notice}</p> : null}

      <section className="mt-6 overflow-hidden rounded-xl border border-neutral-200 bg-white">
        {loading ? (
          <p className="px-4 py-10 text-sm text-neutral-500">Loading activities…</p>
        ) : items.length === 0 ? (
          <p className="px-4 py-10 text-sm text-neutral-500">No recent activities.</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {items.map((item) => (
              <li key={item._id} className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-at-dark">
                  {activityLineWithoutWhen(item.text)}
                  {item.timestamp ? `, ${formatAdminDateTime(item.timestamp)}` : ""}
                </p>
                {item.restorable ? (
                  <button
                    type="button"
                    disabled={Boolean(savingId)}
                    onClick={() => requestRestore(item)}
                    className="shrink-0 rounded-md border border-at-gold bg-white px-3 py-1.5 text-sm font-semibold text-at-dark hover:bg-amber-50 disabled:opacity-40"
                  >
                    {savingId === item._id ? "Restoring…" : "Restore"}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {total > 0 ? (
          <div className="flex flex-col items-center justify-between gap-3 border-t border-neutral-100 px-4 py-3 text-sm text-neutral-500 sm:flex-row">
            <p>{total} {total === 1 ? "activity" : "activities"}</p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => load(page - 1)}
                className="rounded-md border border-neutral-200 bg-white px-2 py-1.5 disabled:opacity-40"
                aria-label="Previous page"
              >
                ‹
              </button>
              <span className="px-2">{page} / {pageCount}</span>
              <button
                type="button"
                disabled={page >= pageCount}
                onClick={() => load(page + 1)}
                className="rounded-md border border-neutral-200 bg-white px-2 py-1.5 disabled:opacity-40"
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          </div>
        ) : null}
      </section>
      {restoreTarget ? (
        <AdminConfirmDialog
          title="Confirm Restore"
          message={`Restore “${restoreTarget.name || "this item"}”?`}
          confirmLabel="Restore"
          busyLabel="Restoring…"
          danger={false}
          busy={Boolean(savingId)}
          onCancel={() => setRestoreTarget(null)}
          onConfirm={confirmRestore}
        />
      ) : null}
    </div>
  );
}

export default AdminActivitiesPage;
